'use client';

import { Button } from '@govinity/ui/components/ui/button';
import { MeetingStatus, resolveMeetingConfig } from '@govinity/validation';
import type { MeetingEventPublicGetResponse } from '@govinity/validation/public';
import { useQuery } from '@tanstack/react-query';
import { MessageSquarePlus } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useState } from 'react';

import api from '@/lib/api';
import { getLoginUrlFromClient } from '@/lib/utils/login-url';
import { useAuthStore } from '@/stores/authStore';
import { CommentDialog } from './comment-dialog';
import { flattenCommentEntries, hasSelectableEntry, publicCommentOptionsQueryKey } from './helpers';
import { useGovernmentContext } from '../../../context';

type PublicCommentsProps = {
  meeting: Pick<MeetingEventPublicGetResponse, 'id' | 'status' | 'agenda'>;
};

export function PublicComments({ meeting }: PublicCommentsProps) {
  const t = useTranslations('PortalPublicComments');
  const { government } = useGovernmentContext();
  const user = useAuthStore((state) => state.user);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const { publicComments } = resolveMeetingConfig(government.meetingConfig);
  const isCommentPossible =
    publicComments.enabled &&
    meeting.status === MeetingStatus.Upcoming &&
    !!meeting.agenda?.publishedAt;

  const { data } = useQuery({
    enabled: isCommentPossible,
    queryKey: publicCommentOptionsQueryKey(government.code, meeting.id),
    queryFn: () => api.governments.publicComments(government.code, meeting.id).getOptions(),
    retry: false,
  });

  if (!isCommentPossible || !hasSelectableEntry(flattenCommentEntries(data?.sections ?? []))) {
    return null;
  }

  if (!user) {
    return (
      <Button asChild size="sm" tooltip={{ content: t('loginRequiredTooltip') }} variant="outline">
        <a href={getLoginUrlFromClient()} rel="noreferrer">
          <MessageSquarePlus aria-hidden="true" />
          {t('commentButton')}
        </a>
      </Button>
    );
  }

  return (
    <>
      <Button onClick={() => setIsDialogOpen(true)} size="sm" variant="outline">
        <MessageSquarePlus aria-hidden="true" />
        {t('commentButton')}
      </Button>
      {isDialogOpen && <CommentDialog onOpenChange={setIsDialogOpen} />}
    </>
  );
}
